import * as React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { motion } from "framer-motion";

import { cn } from "@/lib/utils";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[120] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 duration-300",
      className ?? "bg-black/40 backdrop-blur-[6px]",
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const MotionDialogContent = motion(DialogPrimitive.Content);

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & { 
    hideCloseButton?: boolean; 
    overlayClassName?: string;
    draggable?: boolean;
    dragControls?: any;
    dragListener?: boolean;
    dragConstraints?: any;
  }
>(({ className, children, hideCloseButton, overlayClassName, draggable, dragControls, dragListener, dragConstraints, ...props }, ref) => {
  const ContentComponent: any = draggable ? MotionDialogContent : DialogPrimitive.Content;
  const dragProps = draggable ? {
    drag: true,
    dragControls,
    dragListener,
    dragConstraints,
    dragMomentum: false
  } : {};

  const content = (
    <ContentComponent
      ref={ref as any}
      className={cn(
        draggable
          ? "pointer-events-auto z-[130] grid w-[calc(100%-2rem)] max-w-lg gap-5 border border-border/50 bg-background p-6 sm:p-7 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.03)] rounded-2xl sm:rounded-2xl max-h-[calc(100dvh-var(--safe-top)-var(--safe-bottom)-1.5rem)] overflow-hidden"
          : "fixed left-[50%] top-[50%] z-[130] grid w-[calc(100%-2rem)] max-w-lg translate-x-[-50%] translate-y-[-50%] gap-5 border border-border/50 bg-background p-6 sm:p-7 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.03)] rounded-2xl sm:rounded-2xl duration-300 ease-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-[0.97] data-[state=open]:zoom-in-[0.97] data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] max-h-[calc(100dvh-var(--safe-top)-var(--safe-bottom)-1.5rem)] overflow-hidden",
        className,
      )}
      {...dragProps}
      {...props}
    >
      <div className="min-h-0 flex-1 overflow-auto">{children}</div>
      {!hideCloseButton && (
        <DialogPrimitive.Close className="absolute right-4 top-4 flex items-center justify-center h-8 w-8 rounded-full bg-muted/60 text-muted-foreground/70 transition-all duration-200 hover:bg-muted hover:text-foreground hover:scale-105 focus:outline-none focus:ring-2 focus:ring-ring/30 focus:ring-offset-2 disabled:pointer-events-none">
          <X className="h-3.5 w-3.5" strokeWidth={2.5} />
          <span className="sr-only">Fechar</span>
        </DialogPrimitive.Close>
      )}
    </ContentComponent>
  );

  return (
    <DialogPortal>
      <DialogOverlay className={overlayClassName} />
      {draggable ? (
        <div className="fixed inset-0 z-[130] flex items-center justify-center pointer-events-none">
          {content}
        </div>
      ) : (
        content
      )}
    </DialogPortal>
  );
});
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-2 text-center sm:text-left pr-8", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col-reverse gap-2.5 sm:flex-row sm:justify-end sm:gap-3 pt-1", className)} {...props} />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-bold leading-none tracking-tight text-foreground", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description ref={ref} className={cn("text-[13px] leading-relaxed text-muted-foreground", className)} {...props} />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
};
